import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMoney } from '../../hooks/usePublicSettings';
import { bookingApi, callOutCreditText, bookingContextFrom } from '../customer/bookingApi';

/**
 * ProfessionalCard Component
 * Displays a professional/contractor card with their details, ratings, and booking button
 * 
 * @param {Object} professional - Professional data
 * @param {Object} criteria - What the assistant matched on ({ issueType, severity }); "Book Now" carries it so the
 *                            booking screens quote the visit for that trade and severity instead of guessing
 */
export default function ProfessionalCard({ professional, criteria }) {
  const navigate = useNavigate();
  const [requestState, setRequestState] = useState(null); // null | 'sending' | { ok, message }
  const money = useMoney();
  const {
    id,
    name,
    contactPerson,
    tradeType,
    specializationLevel,
    yearsInBusiness,
    distance,
    rating,
    reviewCount,
    completedJobs,
    emergencyAvailable,
    verified,
    insured,
    // External contractor fields
    isExternal,
    website,
    phone,
    address,
    snippet,
    source,
    // Calendar/Availability fields
    currentlyAvailable,
    activeJobs,
    nextAvailableSlot,
    estimatedResponseTime,
    // Pricing: call-out fees by case at the standard rate (services/pricing.js standardCallOutFees)
    hourlyRate,
    inspectionFee,
    severeInspectionFee,
    emergencyInspectionFee,
    inspectionFeeDeductible,
    currency,
    isCompany
  } = professional;

  // Only real reviews count; the matcher ranks unrated pros with a default that is not shown
  const shownRating = reviewCount > 0 && rating != null ? Number(rating).toFixed(1) : '—';
  const shownDistance = typeof distance === 'number' ? `${distance} km` : distance;
  const inCurrency = { currency };
  const shownHourly = typeof hourlyRate === 'number' ? money(hourlyRate, inCurrency) : hourlyRate;
  const hasFee = v => v !== null && v !== undefined && v !== '';

  return (
    <div className={`bg-white rounded-xl shadow-md p-4 border ${isExternal ? 'border-orange-200 bg-orange-50/30' : 'border-gray-200'} hover:shadow-lg transition-shadow w-full`}>
      {/* External Contractor Badge */}
      {isExternal && (
        <div className="flex items-center gap-1.5 mb-2 text-xs text-orange-700 bg-orange-100 px-2 py-1 rounded-md w-fit">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
          </svg>
          <span className="font-medium">Found Online • {source === 'google_maps' ? 'Google Maps' : 'Web Search'}</span>
        </div>
      )}
      
      {/* Header: Name, Rating, Distance */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 text-base truncate">{name}</h3>
          {contactPerson && <p className="text-sm text-gray-600 truncate">{contactPerson}</p>}
          {phone && <p className="text-sm text-blue-600 truncate">{phone}</p>}
          {address && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{address}</p>}
        </div>
        {!isExternal && (
          <div className="flex flex-col items-end ml-2">
            <div className="flex items-center gap-1 mb-1">
              <svg className="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <span className="font-medium text-gray-900 text-sm">{shownRating}</span>
              <span className="text-xs text-gray-500">({reviewCount || 0})</span>
            </div>
            {shownDistance != null && shownDistance !== '' && <p className="text-xs text-gray-500">{shownDistance}</p>}
          </div>
        )}
        {isExternal && rating && (
          <div className="flex flex-col items-end ml-2">
            <div className="flex items-center gap-1">
              <svg className="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <span className="font-medium text-gray-900 text-sm">{rating}</span>
              {reviewCount > 0 && <span className="text-xs text-gray-500">({reviewCount})</span>}
            </div>
          </div>
        )}
      </div>
      
      {/* Snippet for external contractors */}
      {isExternal && snippet && (
        <p className="text-xs text-gray-600 mb-3 line-clamp-2">{snippet}</p>
      )}

      {/* Trade Type & Specialization */}
      {tradeType && (
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {tradeType}
          </span>
          {specializationLevel && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
              {specializationLevel}
            </span>
          )}
        </div>
      )}

      {/* Availability Status - Only for internal contractors */}
      {!isExternal && (currentlyAvailable !== undefined || nextAvailableSlot || activeJobs !== undefined) && (
        <div className="mb-3 p-2.5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
          <div className="flex flex-col gap-1.5">
            {/* Current Availability */}
            {currentlyAvailable !== undefined && (
              <div className="flex items-center gap-2">
                {currentlyAvailable ? (
                  <>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-sm font-semibold text-green-700">Available Now</span>
                    </div>
                    {estimatedResponseTime && (
                      <span className="text-xs text-gray-600">• {estimatedResponseTime}</span>
                    )}
                  </>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                    <span className="text-sm font-medium text-orange-700">Currently Busy</span>
                  </div>
                )}
              </div>
            )}
            
            {/* Active Jobs */}
            {activeJobs !== undefined && activeJobs > 0 && (
              <div className="flex items-center gap-1.5 text-xs text-gray-600">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{activeJobs} active job{activeJobs > 1 ? 's' : ''} in progress</span>
              </div>
            )}
            
            {/* Next Available Slot */}
            {nextAvailableSlot && !currentlyAvailable && (
              <div className="flex items-center gap-1.5 text-xs text-gray-700">
                <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="font-medium">Next available: {new Date(nextAvailableSlot).toLocaleString('en-US', { 
                  month: 'short', 
                  day: 'numeric', 
                  hour: 'numeric', 
                  minute: '2-digit',
                  hour12: true 
                })}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stats & Badges - Only for internal contractors */}
      {!isExternal && (
        <div className="flex flex-wrap items-center gap-3 mb-3 text-xs text-gray-600">
          <div className="flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span>{yearsInBusiness != null ? `${yearsInBusiness} years` : '—'}</span>
          </div>
          <div className="flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{completedJobs ?? '—'} jobs</span>
          </div>
          {verified && (
            <div className="flex items-center gap-1 text-green-600">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">Verified</span>
            </div>
          )}
          {insured && (
            <div className="flex items-center gap-1 text-blue-600">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <span className="font-medium">Insured</span>
            </div>
          )}
          {emergencyAvailable && (
            <div className="flex items-center gap-1 text-red-600">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">24/7</span>
            </div>
          )}
        </div>
      )}

      {/* ✅ FIX: Pricing Information - Only for internal contractors */}
      {!isExternal && (hasFee(inspectionFee) || hourlyRate) && (
        <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Pricing</h4>
          <div className="space-y-2">
            {/* Hourly Rate */}
            {hourlyRate && (
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">
                  {isCompany ? 'Hourly Rate Range:' : 'Hourly Rate:'}
                </span>
                <span className="font-semibold text-gray-900">{shownHourly}/hr</span>
              </div>
            )}
            
            {/* Call-out fee by case; the chosen time's rate (out of hours, Sunday) applies at booking */}
            {hasFee(inspectionFee) && (
              <div className="flex justify-between items-start text-sm">
                <span className="text-gray-600">Call-out fee:</span>
                <div className="text-right">
                  <span className="font-semibold text-gray-900">{money(inspectionFee, inCurrency)}</span>
                  <div className={`text-xs mt-0.5 ${inspectionFeeDeductible !== false ? 'text-green-600' : 'text-gray-500'}`}>
                    {callOutCreditText(inspectionFeeDeductible !== false)}
                  </div>
                </div>
              </div>
            )}

            {hasFee(severeInspectionFee) && (
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Severe issue:</span>
                <span className="font-semibold text-gray-900">{money(severeInspectionFee, inCurrency)}</span>
              </div>
            )}

            {hasFee(emergencyInspectionFee) && emergencyAvailable && (
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Emergency:</span>
                <span className="font-semibold text-red-600">{money(emergencyInspectionFee, inCurrency)}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-2">
        {!isExternal ? (
          <>
            <button
              onClick={() => {
                // Booking flow with the trade + severity the assistant matched on (priced by the server's quote)
                navigate(`/booking/datetime/${professional.id}`, { state: bookingContextFrom(criteria, professional) });
              }}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors text-sm"
            >
              Book Now
            </button>
            <button
              onClick={() => {
                // Navigate to the new professional profile page
                navigate(`/professionals/${professional.id}`, { state: bookingContextFrom(criteria, professional) });
              }}
              className="px-4 py-2.5 border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors text-sm text-gray-700 font-medium"
            >
              Details
            </button>
          </>
        ) : (
          <>
            <button
              onClick={async () => {
                // Records the request and invites them to join (outreach stays simulated until live)
                setRequestState('sending');
                try {
                  const r = await bookingApi.externalRequest({
                    externalProfessionalId: professional.externalProfessionalId || null,
                    name, phone, website, city: address,
                    trade: criteria?.issueType || professional.trade || tradeType, severity: criteria?.severity
                  });
                  setRequestState({ ok: true, message: r.message });
                } catch (err) {
                  setRequestState({ ok: false, message: err.message });
                }
              }}
              disabled={requestState === 'sending' || requestState?.ok}
              className="disabled:opacity-60 flex-1 bg-orange-600 hover:bg-orange-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
              title="Send invitation and booking request to this contractor"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              {requestState === 'sending' ? 'Sending…' : requestState?.ok ? 'Request sent' : 'Request Booking'}
            </button>
            <button
              onClick={() => {
                // TODO: Copy contact details
                const contactInfo = `${name}\n${phone ? `Phone: ${phone}` : ''}\n${address ? `Address: ${address}` : ''}\n${website ? `Website: ${website}` : ''}`;
                navigator.clipboard.writeText(contactInfo)
                  .then(() => alert('Contact details copied to clipboard'))
                  .catch(() => alert('Could not copy the contact details'));
              }}
              className="px-4 py-2.5 border-2 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors text-sm font-medium flex items-center gap-1"
              title="Copy contact details"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
              </svg>
            </button>
          </>
        )}
      </div>
      
      {requestState && requestState !== 'sending' && (
        <p className={`mt-2 text-xs ${requestState.ok ? 'text-green-700' : 'text-red-600'}`}>{requestState.message}</p>
      )}

    </div>
  );
}

